import React, { useState, useEffect } from 'react';
import tw, { styled } from 'twin.macro';
import { CircleNotch, CheckCircle } from '@phosphor-icons/react';

interface VideoRenderProgressProps {
  isRendering: boolean;
  onCancel: () => void;
  onComplete: () => void;
}

const Container = tw.div`flex flex-col items-center justify-center py-12 px-8`;

const SpinnerIcon = styled(CircleNotch)`
  ${tw`text-indigo-500 mb-4`}
  animation: spin 1s linear infinite;
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const SuccessIcon = tw(CheckCircle)`text-green-500 mb-4`;

const StatusText = tw.p`text-lg font-medium text-gray-800 mb-2 text-center`;

const SubText = tw.p`text-sm text-gray-500 mb-6 text-center`;

const ProgressBarContainer = tw.div`w-full max-w-xs bg-gray-200 rounded-full h-2 mb-6`;

const ProgressBarFill = styled.div<{ progress: number }>`
  ${tw`bg-indigo-500 h-2 rounded-full transition-all duration-500 ease-out`}
  width: ${({ progress }) => progress}%;
`;

const CancelButton = styled.button`
  ${tw`text-sm text-gray-500 hover:text-gray-700 underline transition`}
`;

const DoneButton = styled.button`
  ${tw`px-6 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 transition mt-2`}
`;

const RENDER_MESSAGES = [
  'Preparing your scenes...',
  'Compositing visuals...',
  'Adding transitions...',
  'Rendering your video...',
  'Almost there...',
  'Finalizing...',
];

const VideoRenderProgress: React.FC<VideoRenderProgressProps> = ({
  isRendering,
  onCancel,
  onComplete,
}) => {
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    if (!isRendering) return;

    // Simulate progress (slower — mimics real 30-60s Creatomate render)
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setIsDone(true);
          // Don't auto-close — let user see the result and click "Done"
          return 100;
        }
        return prev + Math.random() * 3 + 1;
      });
    }, 800);

    // Rotate messages
    const messageInterval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % RENDER_MESSAGES.length);
    }, 4000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
    };
  }, [isRendering, onComplete]);

  if (isDone) {
    return (
      <Container>
        <SuccessIcon size={48} weight="fill" />
        <StatusText>Video ready!</StatusText>
        <SubText>
          Your video has been created and added to your ad variants.
        </SubText>
        <DoneButton onClick={onComplete}>Done — go to ad builder</DoneButton>
      </Container>
    );
  }

  return (
    <Container>
      <SpinnerIcon size={48} weight="bold" />
      <StatusText>{RENDER_MESSAGES[messageIndex]}</StatusText>
      <SubText>This usually takes 25–45 seconds</SubText>
      <ProgressBarContainer>
        <ProgressBarFill progress={Math.min(progress, 100)} />
      </ProgressBarContainer>
      <CancelButton onClick={onCancel}>Cancel</CancelButton>
    </Container>
  );
};

export default VideoRenderProgress;
